import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CompanyIntentSummary } from "@/components/CompanyIntentFields/CompanyIntentFields";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { EMPTY_VALUE, formatDate } from "@/i18n/format";
import { intentFromCompany } from "@/lib/companyIntent";
import { countryLabel, countryOptions, regionLabel, regionOptions } from "@/lib/regionOptions";
import { hasTrustBadge, notAssessedLabel, trustBadgeLabel } from "@/lib/trustLabels";
import type {
  CompanyDocument,
  EntityRegistration,
  ModeratorCompanyDetailsRequest,
  ModeratorsCompaniesComplianceCreateBody,
  ModeratorTag,
  ShareholderIdentification,
} from "@/services/moderators/generated/model";
import {
  getModeratorsCompaniesRetrieveQueryKey,
  useModeratorsCompaniesApproveCreate,
  useModeratorsCompaniesComplianceCreate,
  useModeratorsCompaniesDetailsCreate,
  useModeratorsCompaniesFlagCreate,
  useModeratorsCompaniesRetrieve,
  useModeratorsCompaniesSetTagsCreate,
  useModeratorsTagsList,
} from "@/services/moderators/generated/moderators-companies/moderators-companies";
import { useModeratorsReadinessCompaniesList } from "@/services/moderators/generated/moderators-readiness/moderators-readiness";
import {
  useModeratorsVerificationDocumentsApproveCreate,
  useModeratorsVerificationDocumentsRejectCreate,
  useModeratorsVerificationRegistrationsApproveCreate,
  useModeratorsVerificationRegistrationsRejectCreate,
  useModeratorsVerificationShareholdersVerifyCreate,
} from "@/services/moderators/generated/moderators-verification/moderators-verification";
import "./ModeratorPage.css";
import "./ModeratorCompanyDetailPage.css";
import CompanyBillingCard from "./CompanyBillingCard";

const ModeratorCompanyDetailPage = () => (
  <ModeratorLayout>{(moderator) => <DetailContent isSuper={moderator.role === "SUPERMODERATOR"} />}</ModeratorLayout>
);

// Approve / flag / reactivate are super-moderator only (backend enforces
// `IsSuperModerator`); regular moderators see a hint instead of the buttons.
const DetailContent = ({ isSuper }: { isSuper: boolean }) => {
  const { t } = useTranslation(["moderator", "common"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = Number(id);
  const queryClient = useQueryClient();
  const queryKey = getModeratorsCompaniesRetrieveQueryKey(companyId);

  const { data, isLoading, error } = useModeratorsCompaniesRetrieve(companyId, {
    query: { enabled: Number.isFinite(companyId) },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const approveMutation = useModeratorsCompaniesApproveCreate({ mutation: { onSuccess: invalidate } });
  const flagMutation = useModeratorsCompaniesFlagCreate({ mutation: { onSuccess: invalidate } });
  const complianceMutation = useModeratorsCompaniesComplianceCreate({ mutation: { onSuccess: invalidate } });
  const setTagsMutation = useModeratorsCompaniesSetTagsCreate({ mutation: { onSuccess: invalidate } });
  const detailsMutation = useModeratorsCompaniesDetailsCreate({ mutation: { onSuccess: invalidate } });

  const docApprove = useModeratorsVerificationDocumentsApproveCreate({ mutation: { onSuccess: invalidate } });
  const docReject = useModeratorsVerificationDocumentsRejectCreate({ mutation: { onSuccess: invalidate } });
  const regApprove = useModeratorsVerificationRegistrationsApproveCreate({ mutation: { onSuccess: invalidate } });
  const regReject = useModeratorsVerificationRegistrationsRejectCreate({ mutation: { onSuccess: invalidate } });
  const shVerify = useModeratorsVerificationShareholdersVerifyCreate({ mutation: { onSuccess: invalidate } });

  // OQ-11: the moderator decides on every flag whether partners are told.
  const [confirmingFlag, setConfirmingFlag] = useState(false);
  const [notifyPartners, setNotifyPartners] = useState(false);

  const { data: tagsData } = useModeratorsTagsList();
  const allTags = unwrapPaginated<ModeratorTag>(tagsData);

  // WP2.1 — readiness panel: moderator list is small and already fetched
  // elsewhere, so we filter the full list for this company (no per-company
  // endpoint yet).
  const { data: readinessRows = [] } = useModeratorsReadinessCompaniesList();
  const readinessRow = (
    readinessRows as { companyId: number; score: number | null; band: string | null; completedAt: string | null }[]
  ).find((r) => r.companyId === companyId);

  if (!Number.isFinite(companyId)) {
    return <div className="mod-page__error">{t("companyDetail.invalidId")}</div>;
  }
  if (isLoading) return <div className="mod-page__loading">{t("common:states.loading")}</div>;
  if (error || !data?.company) return <div className="mod-page__error">{t("companyDetail.loadError")}</div>;

  const company = data.company;
  const documents = data.documents ?? [];
  const registrations = data.entityRegistrations ?? [];
  const shareholders = data.shareholders ?? [];

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={() => navigate(-1)}>
            ← {t("common:actions.back")}
          </button>
          <h1 className="mod-page__title">{company.companyName}</h1>
          <p className="mod-page__subtitle">{company.companyEmail}</p>
        </div>
        <div className="mod-company-detail__top-actions">
          {!isSuper ? <p className="mod-page__card-meta">{t("companyDetail.superOnlyHint")}</p> : null}
          {isSuper && !company.isApproved ? (
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate({ id: companyId, data: {} })}
            >
              {approveMutation.isPending ? t("companyDetail.approving") : t("companyDetail.approveCompany")}
            </button>
          ) : null}
          {isSuper && company.isApproved && company.isActive ? (
            <button
              type="button"
              className="mod-page__btn mod-page__btn--danger"
              disabled={flagMutation.isPending}
              onClick={() => {
                setNotifyPartners(false);
                setConfirmingFlag(true);
              }}
            >
              {flagMutation.isPending ? t("companyDetail.flagging") : t("companyDetail.flagDeactivate")}
            </button>
          ) : null}
          {isSuper && company.isApproved && !company.isActive ? (
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate({ id: companyId, data: {} })}
            >
              {t("companyDetail.reactivate")}
            </button>
          ) : null}
        </div>
      </header>

      {confirmingFlag && isSuper && company.isApproved && company.isActive ? (
        <section className="mod-page__card" role="dialog" aria-labelledby="flag-confirm-title">
          <h2 id="flag-confirm-title" className="mod-page__card-title">
            {t("companyDetail.flagConfirmTitle", { name: company.companyName })}
          </h2>
          <p className="mod-page__card-meta">{t("companyDetail.flagConfirmBody")}</p>
          <label>
            <input type="checkbox" checked={notifyPartners} onChange={(e) => setNotifyPartners(e.target.checked)} />{" "}
            {t("companyDetail.notifyPartners", { name: company.companyName })}
          </label>
          <div className="mod-company-detail__top-actions">
            <button
              type="button"
              className="mod-page__btn mod-page__btn--danger"
              disabled={flagMutation.isPending}
              onClick={() =>
                flagMutation.mutate(
                  { id: companyId, data: { notifyPartners } },
                  { onSuccess: () => setConfirmingFlag(false) },
                )
              }
            >
              {flagMutation.isPending ? t("companyDetail.flagging") : t("companyDetail.confirmFlag")}
            </button>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--ghost"
              disabled={flagMutation.isPending}
              onClick={() => setConfirmingFlag(false)}
            >
              {t("common:actions.cancel")}
            </button>
          </div>
        </section>
      ) : null}

      {flagMutation.isError ? (
        <div className="mod-page__error" role="alert">
          {extractError(flagMutation.error, t("companyDetail.flagError"))}
        </div>
      ) : null}

      {approveMutation.isError ? (
        <div className="mod-page__error" role="alert">
          {extractError(approveMutation.error, t("companyDetail.approveError"))}
        </div>
      ) : null}

      <section className="mod-page__card mod-company-detail__overview">
        <div className="mod-company-detail__chips">
          <span
            className={`mod-page__chip ${company.isApproved ? "mod-page__chip--success" : "mod-page__chip--warning"}`}
          >
            {company.isApproved ? t("shared.approved") : t("shared.pendingApproval")}
          </span>
          <span className={`mod-page__chip ${company.isActive ? "mod-page__chip--success" : "mod-page__chip--danger"}`}>
            {company.isActive ? t("shared.active") : t("shared.inactive")}
          </span>
          <span
            className={`mod-page__chip ${hasTrustBadge(company) ? "mod-page__chip--success" : ""}`}
            title={t("companyDetail.badgeTooltip")}
          >
            {t("companies.badge", { label: trustBadgeLabel(company) })}
          </span>
          <Link to={`/moderator/kyb/companies/${company.id}`} className="mod-page__chip mod-page__chip--warning">
            <i className="fa-solid fa-id-card-clip" /> {t("companyDetail.openKyb")}
          </Link>
          <span className="mod-page__chip">
            {t("companyDetail.industryChip", { value: company.companyIndustry || EMPTY_VALUE })}
          </span>
          <span className="mod-page__chip">
            {t("companyDetail.countryChip", { value: company.country ? countryLabel(company.country) : EMPTY_VALUE })}
          </span>
          <span className="mod-page__chip">
            {t("companyDetail.regionChip", { value: company.region ? regionLabel(company.region) : EMPTY_VALUE })}
          </span>
          <span className="mod-page__chip" title={t("companyDetail.sourceTooltip")}>
            {t("companies.source", { label: company.leadSourceLabel || t("common:states.unknown") })}
          </span>
          {company.primaryMarket ? (
            <span className="mod-page__chip">
              {t("companyDetail.primaryMarketChip", { value: company.primaryMarket })}
            </span>
          ) : null}
        </div>
        {company.companyDescription ? (
          <p className="mod-company-detail__description">{company.companyDescription}</p>
        ) : null}
      </section>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">{t("companyDetail.tradeProfile")}</h2>
        {company.intentProfileComplete ? null : (
          <p className="mod-page__empty">{t("companyDetail.tradeProfileIncomplete")}</p>
        )}
        <CompanyIntentSummary value={intentFromCompany(company)} />
      </section>

      <section className="mod-page__card">
        <h2 className="mod-page__card-title">{t("readiness.companyPanel.title")}</h2>
        {readinessRow?.band ? (
          <div className="mod-page__card-meta">
            <p>{t("readiness.companyPanel.score", { score: readinessRow.score ?? 0 })}</p>
            <p>
              {t("readiness.companyPanel.band", {
                band:
                  readinessRow.band === "ready"
                    ? t("readiness.bands.ready")
                    : readinessRow.band === "developing"
                      ? t("readiness.bands.developing")
                      : readinessRow.band === "not_ready"
                        ? t("readiness.bands.not_ready")
                        : t("readiness.bands.none"),
              })}
            </p>
            {readinessRow.completedAt ? (
              <p>{t("readiness.companyPanel.takenAt", { date: formatDate(readinessRow.completedAt) })}</p>
            ) : null}
          </div>
        ) : (
          <p className="mod-page__empty">{t("readiness.companyPanel.noAssessment")}</p>
        )}
      </section>
      <CompanyBillingCard companyId={companyId} isSuper={isSuper} />

      <CompanyDetailsEditor
        company={company}
        onSave={(payload) => detailsMutation.mutate({ id: companyId, data: payload })}
        isSaving={detailsMutation.isPending}
      />

      <ComplianceEditor
        company={company}
        onSave={(payload) => complianceMutation.mutate({ id: companyId, data: payload })}
        isSaving={complianceMutation.isPending}
      />

      <TagsEditor
        currentTags={(company.tags ?? []) as unknown as string[]}
        allTags={allTags}
        onSave={(tags) => setTagsMutation.mutate({ id: companyId, data: { tags } })}
        isSaving={setTagsMutation.isPending}
      />

      <DocumentsBlock
        documents={documents}
        onApprove={(docId) => docApprove.mutate({ id: docId })}
        onReject={(docId) => docReject.mutate({ id: docId })}
        pendingId={
          docApprove.isPending ? docApprove.variables?.id : docReject.isPending ? docReject.variables?.id : null
        }
      />

      <RegistrationsBlock
        registrations={registrations}
        onApprove={(regId) => regApprove.mutate({ id: regId })}
        onReject={(regId) => regReject.mutate({ id: regId })}
        pendingId={
          regApprove.isPending ? regApprove.variables?.id : regReject.isPending ? regReject.variables?.id : null
        }
      />

      <ShareholdersBlock
        shareholders={shareholders}
        onVerify={(shId, payload) => shVerify.mutate({ id: shId, data: payload })}
        pendingId={shVerify.isPending ? shVerify.variables?.id : null}
      />
    </div>
  );
};

interface CompanyDetailsEditorProps {
  company: {
    companyName?: string;
    companyIndustry?: string;
    companyDescription?: string;
    country?: string | null;
    region?: string | null;
    primaryMarket?: string;
  };
  onSave: (payload: ModeratorCompanyDetailsRequest) => void;
  isSaving: boolean;
}

const CompanyDetailsEditor = ({ company, onSave, isSaving }: CompanyDetailsEditorProps) => {
  const { t } = useTranslation(["moderator", "common"]);
  const [name, setName] = useState<string>(company.companyName ?? "");
  const [industry, setIndustry] = useState<string>(company.companyIndustry ?? "");
  const [description, setDescription] = useState<string>(company.companyDescription ?? "");
  const [country, setCountry] = useState<string>(company.country ?? "");
  const [region, setRegion] = useState<string>(company.region ?? "");
  const [primaryMarket, setPrimaryMarket] = useState<string>(company.primaryMarket ?? "");

  useEffect(() => {
    setName(company.companyName ?? "");
    setIndustry(company.companyIndustry ?? "");
    setDescription(company.companyDescription ?? "");
    setCountry(company.country ?? "");
    setRegion(company.region ?? "");
    setPrimaryMarket(company.primaryMarket ?? "");
  }, [company]);

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0 && trimmedName.length <= 255;
  const isIndustryValid = industry.length <= 255;
  const isPrimaryMarketValid = primaryMarket.length <= 255;
  const canSave = isNameValid && isIndustryValid && isPrimaryMarketValid;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      companyName: trimmedName,
      companyIndustry: industry.trim() || null,
      companyDescription: description.trim() || null,
      country: country || null,
      region: region || null,
      primaryMarket: primaryMarket.trim() || null,
    });
  };

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("companyDetail.info.title")}</h2>
      <div className="mod-company-detail__compliance-grid">
        <label>
          {t("companyDetail.info.name")}
          <input
            type="text"
            className="mod-page__input"
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
          />
          {!isNameValid ? <span className="mod-page__form-error">{t("companyDetail.info.nameRequired")}</span> : null}
        </label>
        <label>
          {t("companyDetail.info.industry")}
          <input
            type="text"
            className="mod-page__input"
            value={industry}
            maxLength={255}
            onChange={(e) => setIndustry(e.target.value)}
          />
        </label>
        <label>
          {t("companyDetail.info.country")}
          <select className="mod-page__select" value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">{t("companyDetail.info.selectCountry")}</option>
            {countryOptions().map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("companyDetail.info.region")}
          <select className="mod-page__select" value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="">{t("companyDetail.info.selectRegion")}</option>
            {regionOptions().map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("companyDetail.info.primaryMarket")}
          <input
            type="text"
            className="mod-page__input"
            value={primaryMarket}
            maxLength={255}
            onChange={(e) => setPrimaryMarket(e.target.value)}
          />
        </label>
        <label className="mod-company-detail__compliance-grid__full">
          {t("common:labels.description")}
          <textarea
            className="mod-page__input"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
      </div>
      <div className="mod-page__form-row">
        <button
          type="button"
          className="mod-page__btn mod-page__btn--primary"
          disabled={isSaving || !canSave}
          onClick={handleSave}
        >
          {isSaving ? t("common:actions.saving") : t("companyDetail.info.save")}
        </button>
      </div>
    </section>
  );
};

interface ComplianceEditorProps {
  company: {
    overallComplianceStatus?: string;
    overallComplianceScore?: number | null;
    tier1BasicVerification?: number | null;
    tier2StandardCompliance?: number | null;
    tier3AdvancedDueDiligence?: number | null;
  };
  onSave: (payload: ModeratorsCompaniesComplianceCreateBody) => void;
  isSaving: boolean;
}

const ComplianceEditor = ({ company, onSave, isSaving }: ComplianceEditorProps) => {
  const { t } = useTranslation(["moderator", "common"]);
  const [overallStatus, setOverallStatus] = useState<string>(company.overallComplianceStatus || "not_verified");
  const [overallScore, setOverallScore] = useState<number | null>(company.overallComplianceScore ?? null);
  const [tier1, setTier1] = useState<number | null>(company.tier1BasicVerification ?? null);
  const [tier2, setTier2] = useState<number | null>(company.tier2StandardCompliance ?? null);
  const [tier3, setTier3] = useState<number | null>(company.tier3AdvancedDueDiligence ?? null);

  // Re-sync when the underlying data changes (e.g. after a save invalidates).
  useEffect(() => {
    setOverallStatus(company.overallComplianceStatus || "not_verified");
    setOverallScore(company.overallComplianceScore ?? null);
    setTier1(company.tier1BasicVerification ?? null);
    setTier2(company.tier2StandardCompliance ?? null);
    setTier3(company.tier3AdvancedDueDiligence ?? null);
  }, [company]);

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("companyDetail.compliance.title")}</h2>
      <div className="mod-company-detail__compliance-grid">
        <label>
          {t("companyDetail.compliance.overallStatus")}
          <select className="mod-page__select" value={overallStatus} onChange={(e) => setOverallStatus(e.target.value)}>
            <option value="verified">{t("companyDetail.compliance.verified")}</option>
            <option value="not_verified">{t("companyDetail.compliance.notVerified")}</option>
          </select>
        </label>
        <NumberField
          label={t("companyDetail.compliance.overallScore")}
          value={overallScore}
          onChange={setOverallScore}
        />
        <NumberField label={t("companyDetail.compliance.tier1")} value={tier1} onChange={setTier1} />
        <NumberField label={t("companyDetail.compliance.tier2")} value={tier2} onChange={setTier2} />
        <NumberField label={t("companyDetail.compliance.tier3")} value={tier3} onChange={setTier3} />
      </div>
      <div className="mod-page__form-row">
        <button
          type="button"
          className="mod-page__btn mod-page__btn--primary"
          disabled={isSaving}
          onClick={() =>
            onSave({
              overallComplianceStatus:
                overallStatus as ModeratorsCompaniesComplianceCreateBody["overallComplianceStatus"],
              overallComplianceScore: overallScore,
              tier1BasicVerification: tier1,
              tier2StandardCompliance: tier2,
              tier3AdvancedDueDiligence: tier3,
            })
          }
        >
          {isSaving ? t("common:actions.saving") : t("companyDetail.compliance.save")}
        </button>
      </div>
    </section>
  );
};

/** Empty input = not assessed (sent as null); 0 is a real score. */
const NumberField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) => (
  <label>
    {label}
    <input
      type="number"
      min={0}
      max={100}
      className="mod-page__input"
      placeholder={notAssessedLabel()}
      value={value ?? ""}
      onChange={(e) => {
        const raw = e.target.value.trim();
        if (raw === "") {
          onChange(null);
          return;
        }
        const n = Number(raw);
        onChange(Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : null);
      }}
    />
  </label>
);

const TagsEditor = ({
  currentTags,
  allTags,
  onSave,
  isSaving,
}: {
  currentTags: string[];
  allTags: ModeratorTag[];
  onSave: (tags: string[]) => void;
  isSaving: boolean;
}) => {
  const { t } = useTranslation(["moderator", "common"]);
  const [selected, setSelected] = useState<Set<string>>(new Set(currentTags));
  const tagsKey = currentTags.join("|");

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-sync intentionally only when the tag *contents* change, not on every parent render.
  useEffect(() => {
    setSelected(new Set(currentTags));
  }, [tagsKey]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("companyDetail.tags.title")}</h2>
      {allTags.length === 0 ? (
        <p className="mod-page__card-meta">{t("companyDetail.tags.empty")}</p>
      ) : (
        <div className="mod-company-detail__tag-grid">
          {allTags.map((tag) => (
            <label key={tag.id}>
              <input type="checkbox" checked={selected.has(tag.name)} onChange={() => toggle(tag.name)} />
              <span>{tag.name}</span>
            </label>
          ))}
        </div>
      )}
      <div className="mod-page__form-row">
        <button
          type="button"
          className="mod-page__btn mod-page__btn--primary"
          disabled={isSaving}
          onClick={() => onSave(Array.from(selected))}
        >
          {isSaving ? t("common:actions.saving") : t("companyDetail.tags.save")}
        </button>
      </div>
    </section>
  );
};

const DocumentsBlock = ({
  documents,
  onApprove,
  onReject,
  pendingId,
}: {
  documents: CompanyDocument[];
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  pendingId: number | null | undefined;
}) => {
  const { t } = useTranslation(["moderator", "common"]);
  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("companyDetail.documents.title", { count: documents.length })}</h2>
      {documents.length === 0 ? <p className="mod-page__card-meta">{t("companyDetail.documents.empty")}</p> : null}
      {documents.map((doc) => (
        <div key={doc.id} className="mod-company-detail__row">
          <div>
            <div className="mod-company-detail__row-title">
              <a href={doc.fileUrl} target="_blank" rel="noreferrer">
                {doc.fileName}
              </a>
            </div>
            <div className="mod-page__card-meta">
              {t("shared.docMeta", {
                format: doc.fileFormat || EMPTY_VALUE,
                date: formatDate(doc.createdAt),
              })}{" "}
              <span className={`mod-page__chip ${docChipClass(doc.status)}`}>{doc.status}</span>
            </div>
          </div>
          {doc.status === "pending" ? (
            <div className="mod-company-detail__row-actions">
              <button
                type="button"
                className="mod-page__btn mod-page__btn--primary"
                disabled={pendingId === doc.id}
                onClick={() => onApprove(doc.id as number)}
              >
                {t("common:actions.approve")}
              </button>
              <button
                type="button"
                className="mod-page__btn mod-page__btn--danger"
                disabled={pendingId === doc.id}
                onClick={() => onReject(doc.id as number)}
              >
                {t("common:actions.reject")}
              </button>
            </div>
          ) : null}
        </div>
      ))}
    </section>
  );
};

const RegistrationsBlock = ({
  registrations,
  onApprove,
  onReject,
  pendingId,
}: {
  registrations: EntityRegistration[];
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  pendingId: number | null | undefined;
}) => {
  const { t } = useTranslation(["moderator", "common"]);
  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">
        {t("companyDetail.registrations.title", { count: registrations.length })}
      </h2>
      {registrations.length === 0 ? (
        <p className="mod-page__card-meta">{t("companyDetail.registrations.empty")}</p>
      ) : null}
      {registrations.map((reg) => (
        <div key={reg.id} className="mod-company-detail__row">
          <div>
            <div className="mod-company-detail__row-title">
              {reg.country} — {reg.registrationNumber}
            </div>
            <div className="mod-page__card-meta">
              {reg.corporationType || EMPTY_VALUE} ·{" "}
              <span className={`mod-page__chip ${regChipClass(reg.status)}`}>{reg.status}</span>
            </div>
          </div>
          {reg.status !== "active" ? (
            <div className="mod-company-detail__row-actions">
              <button
                type="button"
                className="mod-page__btn mod-page__btn--primary"
                disabled={pendingId === reg.id}
                onClick={() => onApprove(reg.id as number)}
              >
                {t("shared.activate")}
              </button>
              {reg.status !== "inactive" ? (
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--danger"
                  disabled={pendingId === reg.id}
                  onClick={() => onReject(reg.id as number)}
                >
                  {t("shared.markInactive")}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ))}
    </section>
  );
};

const ShareholdersBlock = ({
  shareholders,
  onVerify,
  pendingId,
}: {
  shareholders: ShareholderIdentification[];
  onVerify: (id: number, payload: { idVerified?: "yes" | "no"; uboStatus?: "yes" | "no" }) => void;
  pendingId: number | null | undefined;
}) => {
  const { t } = useTranslation(["moderator", "common"]);
  return (
    <section className="mod-page__card">
      <h2 className="mod-page__card-title">{t("companyDetail.shareholders.title", { count: shareholders.length })}</h2>
      {shareholders.length === 0 ? (
        <p className="mod-page__card-meta">{t("companyDetail.shareholders.empty")}</p>
      ) : null}
      {shareholders.map((sh) => (
        <div key={sh.id} className="mod-company-detail__row">
          <div>
            <div className="mod-company-detail__row-title">
              {sh.name} — {sh.ownershipPercent}%
            </div>
            <div className="mod-page__card-meta">
              {sh.nationality || EMPTY_VALUE} ·{" "}
              <span
                className={`mod-page__chip ${sh.idVerified === "yes" ? "mod-page__chip--success" : "mod-page__chip--warning"}`}
              >
                {sh.idVerified === "yes" ? t("shared.idVerified") : t("shared.idUnverified")}
              </span>{" "}
              <span className={`mod-page__chip ${sh.uboStatus === "yes" ? "mod-page__chip--success" : ""}`}>
                {t("shared.ubo", { status: sh.uboStatus })}
              </span>
            </div>
          </div>
          <div className="mod-company-detail__row-actions">
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={pendingId === sh.id || sh.idVerified === "yes"}
              onClick={() => onVerify(sh.id as number, { idVerified: "yes" })}
            >
              {t("shared.markVerified")}
            </button>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--ghost"
              disabled={pendingId === sh.id}
              onClick={() => onVerify(sh.id as number, { uboStatus: sh.uboStatus === "yes" ? "no" : "yes" })}
            >
              {t("shared.toggleUbo")}
            </button>
          </div>
        </div>
      ))}
    </section>
  );
};

function docChipClass(status: string | undefined): string {
  if (status === "approved") return "mod-page__chip--success";
  if (status === "rejected") return "mod-page__chip--danger";
  return "mod-page__chip--warning";
}

function regChipClass(status: string | undefined): string {
  if (status === "active") return "mod-page__chip--success";
  if (status === "inactive") return "mod-page__chip--danger";
  return "mod-page__chip--warning";
}

function extractError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: unknown } } } | null | undefined;
  const msg = e?.response?.data?.error;
  return typeof msg === "string" ? msg : fallback;
}

export default ModeratorCompanyDetailPage;
